import { FetchResult } from '@apollo/client';
import { ContextMenuInputPoints } from '@thoughtspot/chart-common';
import {
    getDisplayString,
    getStartEpoch,
} from '@thoughtspot/custom-calendar-service';
import {
    convertToMilliSeconds,
    formatDate,
} from '@thoughtspot/date-format-util';
import { getFinalFormattedValue } from '@thoughtspot/formatter-service';
import { translate } from '@thoughtspot/i18n';
import { jsonConstants } from '@thoughtspot/json-constants';
import { formatDataValue } from '@thoughtspot/number-format-util';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { isChangeAnalysisByMeasureEnabled } from '@thoughtspot/session-service';
import {
    hasCustomCalendar,
    isDateColumn,
    isDateNumColumn,
    isDateTimeColumn,
    isTimeColumn,
} from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import omitDeep from 'omit-deep-lodash';
import { BachMutationFunctionOptions } from '/@services/bach/bach.util';
import {
    A3AnalysisTypeEnum,
    AnalysisParam,
    Answer,
    AnswerColumn,
    CcaColumnConfigAction,
    CcaColumnConfigType,
    ChangeDescription,
    ChartViz,
    Column,
    ColumnType,
    Contributor,
    FalconDataType,
    FilterGroup,
    GetA3AnalysisColumnsMutation,
    GetA3AnalysisColumnsMutationVariables,
    InsightAnswer,
    KpiTuple,
    UserPointSelectionInput,
} from '/@services/generated/graphql-types';
import { columnDataType } from '../spotiq/spotiq-constants';
import { AdditionalColumnConfigPayload } from './change-analysis-modal.container';
import { CCAWorkflowOrchestratorProps } from './hooks/useCCAWorkflowOrchestrator';

export const TEST_ID = {
    HEADER_TITLE_TEXT: 'header-title-text',
    HEADER_DESCRIPTION: 'header-description',
    HEADER_PERCENT_CHANGE_ICON: 'header-percent-change-icon',
    HEADER_PERCENT_CHANGE_VALUE: 'header-percent-change-value',
    HEADER_PERCENTAGE_CHANGE_CONTAINER: 'header-percentage-change-container',
    MODAL_HEADER: 'modal-header',
    ITERATIVE_FILTER_BADGE: 'iterative-filter-badge',
    FILTER_BADGE_CONTAINER: 'filter-badge-container',
    CHANGE_DESCRIPTION_CONTAINER: 'change-description-container',
    CHANGE_DESCRIPTION_VALUE: 'change-description-value',
    CHANGE_DESCRIPTION_LOADER: 'change-description-loader',
    CHANGE_ANALYSIS_ERROR: 'change-analysis-error',
    CHANGE_ANALYSIS_ERROR_ICON: 'change-analysis-error-icon',
    CHANGE_ANALYSIS_ERROR_TITLE: 'change-analysis-error-title',
    CHANGE_ANALYSIS_ERROR_DESCRIPTION: 'change-analysis-error-description',
    CHANGE_ANALYSIS_ERROR_RETRY_BUTTON: 'change-analysis-error-retry-button',
    CHANGE_ANALYSIS_MODAL: 'change-analysis-modal',
    INSIGHT_CARD_CONTAINER: 'insight-card-container',
    SUMMARY_TAB_MODAL: 'summary-tab-modal',
    ADDITIONAL_INSIGHTS_CONTAINER: 'additional-insights-container',
    ATTRIBUTES_LIST: 'attributes-list',
    ATTRIBUTE: 'attribute',
    MEASURE: 'measure',
    INSIGHT_CARD_ROOT_LOADER: 'insight-card-root-loader',
    ATTRIBUTE_CONTAINER_LOADER: 'attribute-container-loader',
    CHANGE_ANALYSIS_MODAL_CONTAINER: 'change-analysis-modal-container',
    INSIGHT_CARD_LEGEND: 'insight-card-legend',
    CHANGE_DESCRIPTION_FIRST_POINT_LABEL:
        'change-description-first-point-label',
    CHANGE_DESCRIPTION_FIRST_POINT_VALUE:
        'change-description-first-point-value',
    CHANGE_DESCRIPTION_SECOND_POINT_LABEL:
        'change-description-second-point-label',
    CHANGE_DESCRIPTION_SECOND_POINT_VALUE:
        'change-description-second-point-value',
    CHANGE_ANALYSIS_BY_MEASURE_TAB: 'change-analysis-by-measure-tab',
    FILTER_CHEVRON: 'filter-chevron',
    FILTER_HISTORY_CONTROLLER_WRAPPER: 'filter-history-controller-wrapper',
    CHANGE_ANALYSIS_OVERVIEW_CONTAINER: 'change-analysis-overview-container',
    CHANGE_ANALYSIS_OVERVIEW_DESCRIPTION:
        'change_analysis-overview-description',
    CHANGE_ANALYSIS_SUMMARY_TABLE: 'change-analysis-summary-table',
    CHANGE_ANALYSIS_DESCRIPTION: 'change-analysis-description',
    VIEW_DETAILS_CTA: 'view-details-cta',
    TOP_CONTRIBUTOR_CHANGE: 'top-contributor-change',
    TOP_CONTRIBUTOR_NAME: 'top-contributor-name',
    NO_MAJOR_CONTRIBUTORS: 'no-major-contributors',
    SUMMARY_TABLE_TEXT: 'summary-table-text',
    SUMMARY_TABLE_HEADER: 'summary-table-header',
    CCA_SUMMARY_LOADING: 'cca-summary-loading',
    SOURCE_DETAILS_CONTAINER: 'source-details-container',
    SOURCE_NAME: 'source-name',
    ANSWER_NAME: 'answer-name',
    SOURCE_ICON_CONTAINER: 'source-icon-container',
    SOURCE_LIVEBOARD_ICON: 'source-liveboard-icon',
    SOURCE_WORKSHEET_ICON: 'source-worksheet-icon',
    SOURCE_TABLE_ICON: 'source-table-icon',
    SOURCE_VIEW_ICON: 'source-view-icon',
    FILTER_CONTAINER: 'filter-container',
    ATTRIBUTE_FILTER_VALUE: 'attribute-filter-value',
    FILTER_LIST: 'filter-list',
    SUMMARY_CONTAINER: 'summary-container',
    MEASURE_INSIGHTS_CONTAINER: 'measure-insights-container',
    SUGGESTED_ATTRIBUTES_LIST: 'suggested-attributes-list',
    SUGGESTED_ATTRIBUTE: 'suggested-attribute',
    CCA_SUMMARY_ATTRIBUTE_ERROR: 'cca-summary-attribute-error',
    MORE_INSIGHTS_CONTAINER: 'more-insights-container',
    MORE_INSIGHTS_CONTAINER_TITLE: 'more-insights-container-title',
    INSIGHT_MESSAGE_CONTAINER: 'insight-message-container',
    INSIGHT_MESSAGE_HEADER: 'insight-message-header',
    INSIGHT_MESSAGE_CONTENT: 'insight-message-content',
};

export enum ErrorType {
    SUGGESTED_ATTRIBUTES_FAILURE,
    SUGGESTED_ATTRIBUTES_EMPTY,
    CHANGE_ANALYSIS_NOT_SUPPORTED,
    CHANGE_ANALYSIS_FAILURE,
    ATTRIBUTE_ANALYSIS_FAILURE,
    NO_ATTRIBUTE_SELECTED,
    END_OF_ITERATIVE_CHANGE_ANALYSIS,
    APPLY_FILTERS_FAILED,
    ANSWER_CLONING_FAILED,
    NO_MEASURE_SELECTED,
    CHANGE_ANALYSIS_BY_MEASURE_NOT_SUPPORTED,
}

export interface MeasureInsights {
    columnId: string;
    columnName: string;
    insights: string[];
}

export interface A3AnalysisAttributeInfo {
    guid: string;
    displayName?: string;
    insightAnswer?: InsightAnswer;
    isSelected?: boolean;
    summaryDescription?: string;
    columnType?: ColumnType;
    isMeasureInsight?: boolean;
    measureInsights?: MeasureInsights;
    topContributorInfo?: Contributor;
    suggestedAttributes?: string[];
    otherContributorsInsight?: string;
}

export interface A3AnalysisSyncRequest {
    analysisType: A3AnalysisTypeEnum;
    // in case of change analysis type
    dataRows?: UserPointSelectionInput[] | null;
    selectedColumnsIds?: string[] | null;
    param?: AnalysisParam | null;
    measureColumnId?: string | null;
    isSinglePointKpi?: boolean;
}
export interface ChangeDescriptionPoints {
    formattedSecondPointDate: string | null;
    formattedFirstPointDate: string | null;
}

export interface ChangeDescriptionFormatted {
    firstPointValue: string;
    firstPoint: string;
    secondPointValue: string;
    secondPoint: string;
}

export interface FilterData {
    columnId: string;
    attributeName: string;
    attributeValue: string;
}

export interface CCAOperationStates {
    isFilterBeingApplied: boolean;
    isUndoOpInProgress: boolean;
    isRedoOpInProgress: boolean;
    isResetOpInProgress: boolean;
}

export interface ColorAxisGuidData {
    columnId: string;
    columnGuid: string;
    columnData: AnswerColumn;
}

export interface ShouldCallA3AnalysisColumnsParams {
    isIterativeCCAEnabled: boolean;
    clonedAnswerSessionId: string;
    answerSessionId: string;
    applySuggestionData: Answer;
    isChartSlicedByColor: boolean;
}

interface GetGroupedCCAWorkflowOrchestratorParamsArgs {
    answerSessionId: string;
    clonedAnswerSessionId: string;
    analysisType: A3AnalysisTypeEnum;
    measureColumnId: string;
    analysedColumnIds: string[];
    additionalColumnConfigPayload: AdditionalColumnConfigPayload;
    ignoreCCAColumnConfig: boolean;
    contextMenuPoints: ContextMenuInputPoints;
    chartViz: ChartViz;
    isChartVizDataLoading: boolean;
    applySuggestionData: Answer;
    isApplySuggestionLoading: boolean;
    hasIterationBegan: boolean;
    operationState: CCAOperationStates;
    dataRows: UserPointSelectionInput[];
    setDataRows: React.Dispatch<
        React.SetStateAction<UserPointSelectionInput[]>
    >;
    getA3AnalysisColumns: (
        options?: BachMutationFunctionOptions<
            GetA3AnalysisColumnsMutation,
            Omit<
                GetA3AnalysisColumnsMutationVariables,
                'session' | 'dataPaginationParams'
            >
        >,
    ) => Promise<
        FetchResult<
            GetA3AnalysisColumnsMutation,
            Record<string, any>,
            Record<string, any>
        >
    >;
    setOperationState: React.Dispatch<React.SetStateAction<CCAOperationStates>>;
}

export enum SourceType {
    LIVEBOARD,
    WORKSHEET,
    VIEW,
    TABLE,
}

export const parseSourceType = (sourceTypeStr: string): SourceType => {
    switch (sourceTypeStr) {
        case jsonConstants.metadataType.subType.VIEW:
        case jsonConstants.metadataType.subType.SQL_VIEW:
            return SourceType.VIEW;
        case jsonConstants.metadataType.subType.SYSTEM_TABLE:
            return SourceType.TABLE;
        case jsonConstants.metadataType.subType.WORKSHEET:
            return SourceType.WORKSHEET;
        default:
            return SourceType.VIEW;
    }
};

export interface SourceDetailsInfo {
    sourceName: string;
    sourceType: SourceType;
}

// Either we go from NOT_RECEIVED -> IS_TRUE or NOT_RECEIVED -> IS_FALSE,
// We don't go anywhere from IS_TRUE or IS_FALSE.
export enum ShowCCASummaryFromA3AnalysisSyncResponse {
    NOT_RECEIVED,
    IS_TRUE,
    IS_FALSE,
}

const MAX_ATTRIBUTE_LIMIT = 10;
const MIN_ATTRIBUTE_LIMIT = 1;
const MAX_MEASURE_LIMIT = 3;

/**
 * Populates the submission request for A3 analysis sync
 * @param analysisType
 * @param selectedColumnsIds
 * @param dataRows
 * @param measureColumnId
 */
export const initializeA3AnalysisSyncRequest = (
    analysisType: A3AnalysisTypeEnum,
    selectedColumnsIds: string[],
    dataRows: UserPointSelectionInput[],
    measureColumnId?: string,
    isSinglePointKpi?: boolean,
) => {
    const param: AnalysisParam = {
        analysisDescriptor: [],
    };

    const a3Request: A3AnalysisSyncRequest = {
        analysisType,
        selectedColumnsIds,
        param,
        dataRows,
        measureColumnId,
        isSinglePointKpi,
    };

    return a3Request;
};

export const getTitleForErrorType = (errorType: ErrorType) => {
    switch (errorType) {
        case ErrorType.SUGGESTED_ATTRIBUTES_FAILURE:
            return translate('SUGGESTED_ATTRIBUTES_ERROR_TITLE');
        case ErrorType.SUGGESTED_ATTRIBUTES_EMPTY:
            return translate('SUGGESTED_ATTRIBUTES_EMPTY_TITLE');
        case ErrorType.CHANGE_ANALYSIS_NOT_SUPPORTED:
            return translate('CHANGE_ANALYSIS_NOT_SUPPORTED_TITLE');
        case ErrorType.NO_ATTRIBUTE_SELECTED:
            return translate('NO_ATTRIBUTE_SELECTED_TITLE');
        case ErrorType.END_OF_ITERATIVE_CHANGE_ANALYSIS:
            return translate('END_OF_ITERATIVE_CHANGE_ANALYSIS_ERROR_TITLE');
        case ErrorType.NO_MEASURE_SELECTED:
            return translate('CHANGE_ANALYSIS_NO_MEASURE_SELECTED_TITLE');
        case ErrorType.CHANGE_ANALYSIS_BY_MEASURE_NOT_SUPPORTED:
            return translate('CHANGE_ANALYSIS_BY_MEASURE_NOT_SUPPORTED');
        default:
            return translate('CHANGE_ANALYSIS_ERROR_TITLE');
    }
};

export const getDescriptionForErrorType = (
    errorType: ErrorType,
    attribute?: string,
    errorMessage?: string,
) => {
    switch (errorType) {
        case ErrorType.SUGGESTED_ATTRIBUTES_FAILURE:
            return translate('SUGGESTED_ATTRIBUTES_ERROR_DESCRIPTION');
        case ErrorType.SUGGESTED_ATTRIBUTES_EMPTY:
            return translate('SUGGESTED_ATTRIBUTES_EMPTY_DESCRIPTION');
        case ErrorType.CHANGE_ANALYSIS_NOT_SUPPORTED:
            return errorMessage;
        case ErrorType.ATTRIBUTE_ANALYSIS_FAILURE:
            return translate('ATTRIBUTE_ANALYSIS_ERROR_DESCRIPTION', {
                attribute,
            });
        case ErrorType.NO_ATTRIBUTE_SELECTED:
            return translate('NO_ATTRIBUTE_SELECTED_DESCRIPTION');
        case ErrorType.END_OF_ITERATIVE_CHANGE_ANALYSIS:
            return translate(
                'END_OF_ITERATIVE_CHANGE_ANALYSIS_ERROR_DESCRIPTION',
            );
        case ErrorType.APPLY_FILTERS_FAILED:
            return translate('APPLY_FILTER_ERROR_DESCRIPTION');
        case ErrorType.ANSWER_CLONING_FAILED:
            return translate('ANSWER_CLONING_FAILED_ERROR_DESCRIPTION');
        case ErrorType.NO_MEASURE_SELECTED:
            return translate('CHANGE_ANALYSIS_NO_MEASURE_SELECTED_DESCRIPTION');
        default:
            return translate('CHANGE_ANALYSIS_ERROR_DESCRIPTION');
    }
};

export const shouldShowTryAgainButton = (errorType: ErrorType) => {
    switch (errorType) {
        case ErrorType.SUGGESTED_ATTRIBUTES_FAILURE:
            return false;
        case ErrorType.SUGGESTED_ATTRIBUTES_EMPTY:
            return false;
        case ErrorType.CHANGE_ANALYSIS_NOT_SUPPORTED:
            return false;
        case ErrorType.NO_ATTRIBUTE_SELECTED:
            return false;
        case ErrorType.END_OF_ITERATIVE_CHANGE_ANALYSIS:
            return false;
        case ErrorType.ANSWER_CLONING_FAILED:
            return false;
        case ErrorType.NO_MEASURE_SELECTED:
            return false;
        case ErrorType.CHANGE_ANALYSIS_BY_MEASURE_NOT_SUPPORTED:
            return false;
        default:
            return true;
    }
};

/**
 * Get path to the svg illustration from radiant-styles module
 *
 * @param filename svg file name
 */
export const getErrorIconSVGPath = (filename: string) =>
    `/node_modules/@thoughtspot/radiant-styles/public/img/illustrations/${filename}.svg`;

const summaryTabId = _.uniqueId();

export const getSummaryTabId = () => {
    return summaryTabId;
};

// This function takes a list of customizeAttributes and an optional list of columns.
// getCustomizeAttributesList returns a list of customizeAttributes where the 'isSelected' property is updated based on the columns passed or the 'isSuggested' property of customizeAttributes.
export const getCustomizeAttributesList = (
    customizeAttributesList: columnDataType[],
    columns: string[] = [],
) => {
    const clonedCustomizeAttributesList: columnDataType[] = _.cloneDeep(
        customizeAttributesList,
    );
    return _.map(
        clonedCustomizeAttributesList,
        (attribute: columnDataType) => ({
            ...attribute,
            isSelected: columns.length
                ? columns.includes(attribute.columnId) // Checking if the columns array includes the current attribute's columnId.
                : !!attribute.isSuggested, // If the columns array is empty, check if the attribute's isSuggested property is truthy.
        }),
    );
};

/**
 * this function can be used when needed to remove the configType property from the object
 * @param obj - any object
 * @returns object without configType
 */
export function removeConfigType(obj: Record<string, any>) {
    return omitDeep(_.cloneDeep(obj), 'configType');
}

/**
 * Initializes a column configuration request based on the provided action and selected attributes map.
 *
 * @param {CcaColumnConfigAction} action - The action for the column configuration request.
 * @param {Map<string, A3AnalysisAttributeInfo>} selectedAttributesMap - The map of selected attributes.
 * @param {boolean} isResetPerformed - Indicates whether a reset operation was performed.
 * @returns {Object} The column configuration request object.
 */
export const initializeCCAColumnConfig = (
    action: CcaColumnConfigAction,
    selectedAttributesMap: Map<string, A3AnalysisAttributeInfo>,
    isResetPerformed = false,
    additionalColumnConfigPayload: AdditionalColumnConfigPayload | null,
) => {
    // Initialize an array to store selected column IDs
    const selectedColumnIds: string[] = [];

    // Check if a reset operation wasn't performed and there are selected attributes
    if (!isResetPerformed && selectedAttributesMap.size)
        selectedAttributesMap.forEach(attribute => {
            if (attribute?.isSelected) selectedColumnIds.push(attribute?.guid);
        });

    // Construct and return the column configuration request object
    return {
        ccaColumnConfigRequest: {
            action,
            ccaColumnConfig: {
                columns: selectedColumnIds,
                ...(additionalColumnConfigPayload && {
                    ...removeConfigType(additionalColumnConfigPayload),
                    type: additionalColumnConfigPayload?.configType,
                }),
            },
            ...(isResetPerformed && { isResetPerformed }), // Add isResetPerformed property if reset was performed
        },
    };
};

export const isCCAColumnConfigResetBtnDisabled = (
    columnConfigurationType: CcaColumnConfigType,
    hasEditPermission: boolean,
) => {
    // Conditions to determine if the reset button should be disabled
    return (
        // Disable if the column configuration type is 'UBR'
        columnConfigurationType === CcaColumnConfigType.Ubr ||
        // Disable if the column configuration type is 'Chart' and without chart edit permission
        (columnConfigurationType === CcaColumnConfigType.Chart &&
            !hasEditPermission)
    );
};
/**
 * Calculates and returns a formatted date interval (as displayed in the KPI chart)
 * based on the provided context menu points.
 *
 * @param {ContextMenuInputPoints} contextMenuPoints - Selected point data.
 * @returns {ChangeDescriptionPoints} - Formatted date interval.
 */
export const getFormattedDateIntervalPoints = (
    contextMenuPoints: ContextMenuInputPoints,
): ChangeDescriptionPoints => {
    const changeDescriptionPoints: ChangeDescriptionPoints = {
        formattedFirstPointDate: null,
        formattedSecondPointDate: null,
    };
    // To reflect the change description date format to be the same as in KPI.
    // Scenarios for Triggering CCA:
    // - Kpi:
    //   1. Normal
    //   2. By Clicking on two points
    // - Other charts:
    //   1. By Clicking on two points

    const firstSelectedAttributeDetails =
        _.get(contextMenuPoints, 'selectedPoints.0.selectedAttributes.0') ||
        _.get(contextMenuPoints, 'selectedPoints.0.deselectedAttributes.0');
    const secondSelectedAttributeDetails =
        _.get(contextMenuPoints, 'selectedPoints.1.selectedAttributes.0') ||
        _.get(contextMenuPoints, 'selectedPoints.1.deselectedAttributes.0');

    const firstAttributeColumn = _.get(firstSelectedAttributeDetails, 'column');
    const secondAttributeColumn = _.get(
        secondSelectedAttributeDetails,
        'column',
    );

    if (
        isDateNumColumn(firstAttributeColumn) ||
        isDateTimeColumn(firstAttributeColumn) ||
        isDateColumn(firstAttributeColumn) ||
        isTimeColumn(firstAttributeColumn)
    ) {
        const firstAttributeValue = _.get(
            firstSelectedAttributeDetails,
            'value',
        );
        const secondAttributeValue = _.get(
            secondSelectedAttributeDetails,
            'value',
        );
        changeDescriptionPoints.formattedSecondPointDate = getFinalFormattedValue(
            firstAttributeValue,
            firstAttributeColumn,
        );
        changeDescriptionPoints.formattedFirstPointDate = getFinalFormattedValue(
            secondAttributeValue,
            secondAttributeColumn,
        );
    }

    return changeDescriptionPoints;
};
/**
 * Returns the formatted change description information as an object
 * @param changeDescriptionList
 * @param changeDescriptionPoints
 * @param measureColumn
 * @returns ChangeDescriptionFormatted
 */
export const getFormattedChangeDescriptionPoints = (
    changeDescriptionList: ChangeDescription,
    changeDescriptionPoints: ChangeDescriptionPoints,
    measureColumn: AnswerColumn,
): ChangeDescriptionFormatted => {
    if (!changeDescriptionList || !changeDescriptionPoints) {
        return {
            firstPoint: null,
            firstPointValue: null,
            secondPoint: null,
            secondPointValue: null,
        };
    }
    const {
        firstAggregate,
        firstValueList,
        secondAggregate,
        secondValueList,
    } = changeDescriptionList;

    // For date and time columns
    const {
        formattedFirstPointDate,
        formattedSecondPointDate,
    } = changeDescriptionPoints;
    // If measureColumn is present, get the formatting from that column
    const params: ChangeDescriptionFormatted = {
        secondPointValue: measureColumn
            ? getFinalFormattedValue(secondAggregate, measureColumn)
            : formatDataValue(secondAggregate, {
                  noShorten: false,
              }),
        secondPoint: formattedSecondPointDate || secondValueList,
        firstPointValue: measureColumn
            ? getFinalFormattedValue(firstAggregate, measureColumn)
            : formatDataValue(firstAggregate, {
                  noShorten: false,
              }),
        firstPoint: formattedFirstPointDate || firstValueList,
    };

    return params;
};

// For legacy version of cca modal
export const getChangeDescriptionString = (
    changeDescriptionList: ChangeDescription,
    changeDescriptionPoints: ChangeDescriptionPoints,
    measureColumn: AnswerColumn,
) => {
    const {
        firstPoint: secondPoint,
        firstPointValue: secondPointValue,
        secondPoint: firstPoint,
        secondPointValue: firstPointValue,
    } = getFormattedChangeDescriptionPoints(
        changeDescriptionList,
        changeDescriptionPoints,
        measureColumn,
    );

    return translate('CHANGE_ANALYSIS_MODAL_DESCRIPTION', {
        secondPoint,
        secondPointValue,
        firstPoint,
        firstPointValue,
    });
};

export const getLimitAlertMessage = (
    selectedAttributesCount: number,
    selectedMeasuresCount: number,
) => {
    const attributeLimitExceeded =
        selectedAttributesCount > MAX_ATTRIBUTE_LIMIT;
    const attributeLimitNotMet = selectedAttributesCount < MIN_ATTRIBUTE_LIMIT;
    const measureLimitExceeded = selectedMeasuresCount > MAX_MEASURE_LIMIT;
    const noSelectionMade = attributeLimitNotMet && selectedMeasuresCount < 1;

    if (attributeLimitExceeded) {
        // If the attribute limit is exceeded, return the corresponding alert message
        return translate('CUSTOMIZE_ATTRIBUTES_MAX_LIMIT_ERROR_TEXT');
    }

    if (isChangeAnalysisByMeasureEnabled()) {
        if (measureLimitExceeded) {
            // If the measure limit is exceeded in Change Analysis by Measure mode, return the corresponding alert message
            return translate('CUSTOMIZE_MEASURES_MAX_LIMIT_ERROR_TEXT');
        }

        if (noSelectionMade) {
            // If no selection is made and in Change Analysis by Measure mode, return the minimum limit alert for either measures or attributes
            return translate(
                'CUSTOMIZE_MEASURES_OR_ATTRIBUTES_MIN_LIMIT_ERROR_TEXT',
            );
        }
    } else if (attributeLimitNotMet) {
        // If the attribute limit is not met and not in Change Analysis by Measure mode, return the minimum limit alert
        return translate('CUSTOMIZE_ATTRIBUTES_MIN_LIMIT_ERROR_TEXT');
    }

    return ''; // Return an empty string if no conditions are met
};

export const getSourceName = (contextMenuPoints: ContextMenuInputPoints) => {
    return _.get(
        contextMenuPoints,
        'selectedPoints.0.selectedMeasures.0.column.referencedTables.0.displayName',
    );
};

export const getSourceId = (contextMenuPoints: ContextMenuInputPoints) => {
    return _.get(
        contextMenuPoints,
        'selectedPoints.0.selectedMeasures.0.column.referencedTables.0.guid',
    );
};

const measureTabId = `measure${_.uniqueId()}`;

export const getMeasureTabId = () => {
    return measureTabId;
};

export const getMeasureInsightsList = (
    attributesMap: Map<string, A3AnalysisAttributeInfo>,
) => {
    // Find the single object from attributesMap that contains insightAnswer for selected measure attributes
    const measureWithInsights = Array.from(attributesMap.values()).find(
        item =>
            item.columnType === ColumnType.Measure &&
            item.isSelected &&
            item.insightAnswer?.measureInsights,
    );

    // If no insights are found, return an empty array
    if (!measureWithInsights?.insightAnswer?.measureInsights) return [];

    // Map the measure insights to extract relevant information for each insight
    return measureWithInsights.insightAnswer.measureInsights
        .map(insight => {
            // Get the corresponding attribute information using the drillColumnId from insight
            const item = attributesMap.get(insight.drillColumnId);
            return item
                ? {
                      columnId: item.guid,
                      columnName: item.displayName,
                      insights: insight.values || [],
                  }
                : null;
        })
        .filter(Boolean); // Remove any null values
};

export const getMeasureInsightInfo = (
    attributesMap: Map<string, A3AnalysisAttributeInfo>,
): any => {
    // Find the selected measure attribute that has insightAnswer and measureInsights
    const measure = Array.from(attributesMap.values()).find(
        item =>
            item.columnType === ColumnType.Measure &&
            item.isSelected &&
            item.insightAnswer?.measureInsights,
    );
    // Return the measure attribute object, or undefined if not found
    return measure;
};

export const getSelectedMeasureIds = (columns: columnDataType[]) =>
    columns
        .filter(
            item => item?.columnType === ColumnType.Measure && item.isSelected,
        )
        .map(item => item.columnId);

export const getDateColumn = (columns: Column[]) =>
    columns?.find(item => item?.dataType === ColumnType.Date);

/**
 * Processes the first string in the provided data array by extracting the value
 * inside the <p> tag, formatting that value, and reconstructing the string
 * without the <p> tag but with the updated value.
 *
 * @param {string[]} data - An array of strings, where the first string contains an HTML <p> tag inside a <span>.
 * @returns {string} - The modified string with the value doubled and the <p> tag removed.
 */
export const getFormattedMeasureInsight = (
    insight: string,
    measureColumn: AnswerColumn,
) => {
    const pTagContentMatch = insight.match(/<p[^>]*>(.*?)<\/p>/);

    if (pTagContentMatch) {
        // Extract the value inside the <p> tag and format it
        const value = getFinalFormattedValue(
            parseFloat(pTagContentMatch[1]),
            measureColumn,
        );

        // Replace the <p> tag with the new value
        const result = insight.replace(/<p[^>]*>.*?<\/p>/, value.toString());

        return result;
    }

    return insight;
};

export const containsOnlyMeasures = (
    attributesMap: Map<string, A3AnalysisAttributeInfo>,
): boolean => {
    // Use the every method on filtered selected attributes to check if all have columnType as 'MEASURE'
    return Array.from(attributesMap.values())
        .filter(attribute => attribute?.isSelected) // Filter by isSelected: true
        .every(attribute => attribute?.columnType === ColumnType.Measure); // Check if all filtered attributes have columnType: 'MEASURE'
};

export const getSourceType = (
    pinboard?: { id: string },
    sourceDetails?: { type: string }[],
): SourceType | null => {
    if (pinboard?.id) return SourceType.LIVEBOARD;
    if (sourceDetails?.length) return parseSourceType(sourceDetails[0].type);
    return null;
};

export const getSourceIconDetails = (
    sourceType: SourceType,
): { iconId: IconID; iconTestId: string } => {
    switch (sourceType) {
        case SourceType.LIVEBOARD:
            return {
                iconId: IconID.PINBOARD_NEW,
                iconTestId: TEST_ID.SOURCE_LIVEBOARD_ICON,
            };
        case SourceType.WORKSHEET:
            return {
                iconId: IconID.SQL_WORKSHEET,
                iconTestId: TEST_ID.SOURCE_WORKSHEET_ICON,
            };
        case SourceType.TABLE:
            return {
                iconId: IconID.SQL_TABLE,
                iconTestId: TEST_ID.SOURCE_TABLE_ICON,
            };
        default:
            return {
                iconId: IconID.PINBOARD_NEW,
                iconTestId: TEST_ID.SOURCE_VIEW_ICON,
            };
    }
};

export const isFilterPresentInStack = (
    appliedAttributeFilterStack: FilterData[],
    attributeValue: string,
    attributeName: string,
    columnId: string,
) => {
    return appliedAttributeFilterStack.some(
        attribute =>
            attribute.attributeValue === attributeValue &&
            attribute.attributeName === attributeName &&
            attribute.columnId === columnId,
    );
};

export const getColorValueFromContextMenuPoint = (
    contextMenuPoints: ContextMenuInputPoints,
    colorColumnId: string,
): string | undefined => {
    if (!contextMenuPoints?.selectedPoints?.length) {
        return undefined;
    }

    const selectedAttributes =
        contextMenuPoints.selectedPoints[0]?.selectedAttributes;
    if (!selectedAttributes?.length) {
        return undefined;
    }

    const matchingAttribute = selectedAttributes.find(
        attributeData => attributeData?.column?.id === colorColumnId,
    );
    return matchingAttribute?.value;
};

export const getColorAxisDataFromChartViz = (
    chartViz: ChartViz,
): ColorAxisGuidData[] => {
    const colorAxisData = chartViz?.config?.axisConfig?.[0]?.color ?? [];

    return colorAxisData.map(colorAxis => ({
        columnId: colorAxis.id,
        columnGuid: colorAxis.referencedColumns?.[0]?.guid,
        columnData: colorAxis,
    }));
};

/**
 * Determines whether the given chart is sliced by color.
 *
 * Returns:
 * - true:  if the chart is configured with color slicing
 * - false: if the chart exists but is not sliced by color
 * - null:  if the chartViz object is null or undefined (e.g., data not yet loaded),
 *          meaning it's not currently possible to determine the whether the chart is sliced by color
 * Note:
 * The calling function is responsible for handling the `null` case explicitly,
 */
export const isColorSlicedChart = (chartViz: ChartViz): boolean | null => {
    if (!chartViz) return null;
    const color = chartViz?.config?.axisConfig?.[0]?.color;
    return Array.isArray(color) && color.length > 0;
};

export const shouldCallA3AnalysisColumns = ({
    isIterativeCCAEnabled,
    clonedAnswerSessionId,
    answerSessionId,
    applySuggestionData,
    isChartSlicedByColor,
}: ShouldCallA3AnalysisColumnsParams): boolean => {
    // If iterative CCA is not enabled, always call the a3AnalysisColumns API.
    if (!isIterativeCCAEnabled) return true;

    const isClonedSession = clonedAnswerSessionId !== answerSessionId;

    // The a3AnalysisColumns API should be called only for a cloned answer session
    // when iterative CCA is enabled and we can determine if the chart is sliced by color.
    if (!isClonedSession || _.isNull(isChartSlicedByColor)) return false;

    if (isChartSlicedByColor) {
        // For color-sliced charts:
        // Initiate the CCA API call only when the initial applySuggestion API call
        // has completed successfully — indicated by non-null applySuggestionData.
        return !_.isNil(applySuggestionData);
    }

    // For non-color-sliced charts, call the API for cloned sessions with iterative CCA enabled.
    return true;
};

export const getGroupedCCAWorkflowOrchestratorParams = ({
    answerSessionId,
    clonedAnswerSessionId,
    analysisType,
    measureColumnId,
    analysedColumnIds,
    additionalColumnConfigPayload,
    ignoreCCAColumnConfig,
    contextMenuPoints,
    chartViz,
    isChartVizDataLoading,
    applySuggestionData,
    isApplySuggestionLoading,
    hasIterationBegan,
    operationState,
    dataRows,
    setDataRows,
    getA3AnalysisColumns,
    setOperationState,
}: GetGroupedCCAWorkflowOrchestratorParamsArgs): CCAWorkflowOrchestratorProps => {
    return {
        sessionInfo: { answerSessionId, clonedAnswerSessionId },
        analysisInfo: {
            analysisType,
            measureColumnId,
            analysedColumnIds,
            additionalColumnConfigPayload,
            ignoreCCAColumnConfig,
            contextMenuPoints,
        },
        chartState: { chartViz, isChartVizDataLoading },
        applySuggestionState: { applySuggestionData, isApplySuggestionLoading },
        iterationState: {
            hasIterationBegan,
            operationState,
            setOperationState,
        },
        dataHandlers: { dataRows, setDataRows },
        orchestratorActions: { getA3AnalysisColumns },
    };
};

export const getNewVizId = (
    oldToNewVisualizationMap: any,
    oldVizId: string,
) => {
    // Obtain the new visualization ID corresponding to the original visualization
    // from the cloned answer.
    return oldToNewVisualizationMap?.find(
        (oldToNewVizTuple: any) => oldToNewVizTuple.oldVizId === oldVizId,
    )?.newVizId;
};

/**
 * Extracts the date filter ID from filter groups
 *
 * This function finds the first filter group with date type columns and returns either:
 * - The formula ID if it exists
 * - The GUID of the first referenced column if no formula ID
 * - Null if no date filter group found
 *
 * @param filterGroups - Array of filter groups to search through
 * @returns The formula ID, referenced column GUID, or null
 */
export const extractDateFilterId = (
    filterGroups: FilterGroup[],
): string | null => {
    // Find the first filter group that contains date type columns
    const dateGroup = filterGroups?.find(
        group => group?.columnInfo?.dataType === FalconDataType.Date,
    );

    // Return null if no date group found
    if (!dateGroup) return null;

    // If date group has no formula ID, return the GUID of first referenced column
    if (dateGroup.columnInfo.formulaId) {
        return dateGroup.columnInfo.formulaId || null;
    }

    return dateGroup.columnInfo.referencedColumns[0]?.guid || null;
};

/**
 * Generates data rows for date columns based on KPI tuples.
 *
 * This function creates properly formatted data structures for date columns in the change analysis
 * context, handling both regular KPI tuples and aggregated KPI tuples that have start and end epochs.
 *
 * @param dateColumnId - The ID of the date column
 * @param effectiveTuple - The reference tuple (previous/comparison value)
 * @param currentTuple - The current tuple
 * @returns An object representing the data row for the date column with properly formatted epoch ranges
 */
export const getDataRowsForDateColumn = (
    dateColumnId: string,
    effectiveTuple: KpiTuple,
    currentTuple: KpiTuple,
): UserPointSelectionInput | null => {
    // Input validation
    if (!dateColumnId || !effectiveTuple || !currentTuple) {
        return null;
    }

    try {
        // Prepare data structure for the date column
        return {
            columnId: dateColumnId,
            dataValue: [
                {
                    epochRange: {
                        startEpoch: parseInt(effectiveTuple.time, 10),
                        endEpoch: parseInt(effectiveTuple.endTime, 10),
                    },
                },
                {
                    epochRange: {
                        startEpoch: parseInt(currentTuple.time, 10),
                        endEpoch: parseInt(currentTuple.endTime, 10),
                    },
                },
            ],
        };
    } catch (error) {
        return null;
    }
};

/**
 * This function formats the date or time column attribute's value in a format suitable for feeding into applySuggestions.
 * @param column - The column object
 * @param value - The value to process. Can be an epoch timestamp (number)
 * or an object from which epoch/display string can be extracted.
 * @returns The formatted value, or null if the column is not a date or time column.
 */
export const getFormattedDateForApplySuggestions = (
    column: AnswerColumn,
    value: any,
): string | null => {
    // If the column is not a date or time column, return null
    if (!isDateColumn(column) && !isTimeColumn(column)) {
        return null;
    }

    if (hasCustomCalendar(column)) {
        const displayString = getDisplayString(value);

        if (displayString) {
            return displayString;
        }

        const startEpoch = getStartEpoch(value);

        if (startEpoch) {
            // convertToMilliSeconds has a fallback to return null if the input is not a number
            const epochInMilliSeconds = convertToMilliSeconds(startEpoch);

            if (!epochInMilliSeconds) {
                return null;
            }

            const epochConvertedToFormattedDate = formatDate(
                epochInMilliSeconds,
                'MMMM YYYY',
                true,
            );

            return String(epochConvertedToFormattedDate).toLowerCase();
        }
    }

    const epochInMilliSeconds = convertToMilliSeconds(value);

    if (!epochInMilliSeconds) {
        return null;
    }

    const epochConvertedToFormattedDate = formatDate(
        epochInMilliSeconds,
        'MMMM YYYY',
        true,
    );

    return String(epochConvertedToFormattedDate).toLowerCase();
};

export const shouldDisableCCAForDifferentColorValues = (
    chartViz: ChartViz,
    contextMenuPoints: ContextMenuInputPoints,
): { shouldDisable: boolean; reason?: string } => {
    const isColorSliced = isColorSlicedChart(chartViz);

    if (!isColorSliced) {
        return { shouldDisable: false };
    }

    const colorAxisData = getColorAxisDataFromChartViz(chartViz);

    if (!colorAxisData || colorAxisData.length === 0) {
        return { shouldDisable: false };
    }

    // Need exactly 2 points for comparison
    if (
        !contextMenuPoints?.selectedPoints ||
        contextMenuPoints.selectedPoints.length !== 2
    ) {
        return { shouldDisable: false };
    }

    const firstPoint = contextMenuPoints.selectedPoints[0];
    const secondPoint = contextMenuPoints.selectedPoints[1];

    if (!firstPoint?.selectedAttributes || !secondPoint?.selectedAttributes) {
        return { shouldDisable: false };
    }

    // Helper function to get color value for a given color axis from a point
    const getColorValue = (
        selectedAttributes: typeof firstPoint.selectedAttributes,
        colorAxisId: string,
    ) => {
        return selectedAttributes.find(attr => attr?.column?.id === colorAxisId)
            ?.value;
    };

    // Check all color axes - if any color axis has different values between
    // the two points, disable CCA
    const hasDifferentColorValues = colorAxisData.some(colorAxis => {
        const colorAxisId = colorAxis.columnId;

        if (!colorAxisId) {
            return false;
        }

        const firstPointColorValue = getColorValue(
            firstPoint.selectedAttributes,
            colorAxisId,
        );

        const secondPointColorValue = getColorValue(
            secondPoint.selectedAttributes,
            colorAxisId,
        );

        // Both points must have color values for this axis
        if (
            firstPointColorValue === undefined ||
            secondPointColorValue === undefined
        ) {
            return false;
        }

        // Compare color values using deep equality check,
        // this handles cases where values might be objects, arrays, or primitives
        const areValuesEqual = _.isEqual(
            firstPointColorValue,
            secondPointColorValue,
        );

        // Return true if color values differ for this color axis
        return !areValuesEqual;
    });

    // Disable if color values differ for any color axis
    if (hasDifferentColorValues) {
        return {
            shouldDisable: true,
            reason: translate(
                'CHANGE_ANALYSIS_ACROSS_DIFFERENT_COLOR_VALUES_NOT_SUPPORTED',
            ),
        };
    }

    return { shouldDisable: false };
};
